/**
 * Timer per le schede nascoste o in background.
 *
 * Chrome rallenta i timer della pagina quando la scheda è nascosta: setTimeout/setInterval
 * scendono a 1 volta al secondo, e con l'"intensive throttling" (dopo 5 minuti, o con il
 * Risparmio energetico) a 1 volta al minuto. requestAnimationFrame si ferma del tutto.
 * Il ciclo che aggiorna volume e direzione delle voci andrebbe quindi a scatti.
 *
 * I timer di un Dedicated Worker non subiscono quel rallentamento: il worker manda un
 * messaggio a ogni tick e il main thread lo riceve anche in background (i messaggi non
 * vengono rallentati come i timer). Il worker è creato da un Blob, senza file separati.
 * Dove i Worker non esistono (test, browser molto vecchi) si ripiega su setInterval.
 */
export interface Ticker {
  stop(): void;
}

const WORKER_SOURCE = `
let id = null;
self.onmessage = (e) => {
  if (id !== null) clearInterval(id);
  id = null;
  if (e.data && e.data.interval > 0) id = setInterval(() => self.postMessage(0), e.data.interval);
};
`;

let workerUrl: string | null = null;

function createWorker(): Worker | null {
  try {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return null;
    workerUrl ??= URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    return new Worker(workerUrl);
  } catch {
    return null; // es. CSP che vieta i worker da blob:
  }
}

/** Chiama `onTick` ogni `intervalMs` ms, anche con la scheda nascosta. */
export function startBackgroundTicker(intervalMs: number, onTick: () => void): Ticker {
  const worker = createWorker();
  if (worker) {
    worker.onmessage = () => onTick();
    worker.postMessage({ interval: intervalMs });
    return {
      stop() {
        worker.postMessage({ interval: 0 });
        worker.terminate();
      },
    };
  }
  const id = setInterval(onTick, intervalMs);
  return { stop: () => clearInterval(id) };
}

export interface TimeoutHandle {
  cancel(): void;
}

/**
 * Come setTimeout, ma basato sul worker: non viene ritardato da Chrome con la scheda nascosta
 * o la finestra ridotta a icona (dove un setTimeout della pagina può slittare fino a 1 minuto).
 */
export function backgroundTimeout(ms: number, fn: () => void): TimeoutHandle {
  let done = false;
  const ticker = startBackgroundTicker(Math.max(1, ms), () => {
    if (done) return;
    done = true;
    ticker.stop();
    fn();
  });
  return {
    cancel() {
      if (done) return;
      done = true;
      ticker.stop();
    },
  };
}
